import { FlightCard, PromoData, Sectors } from '@/src/types/flight'
import { ExtractedFlightCardProps } from './flightCardProps'

// Utility to extract layovers for a flight
export function getLayoversForFlight(
    flight: FlightCard,
    sectors: Sectors
): Array<{ durationMins: number; durationStr: string; city: string }> {
    if (!flight.sectorKeys || !sectors) return []
    const layovers: Array<{ durationMins: number; durationStr: string; city: string }> = []
    flight.sectorKeys.forEach((key: string) => {
        const sector = sectors[key]
        if (!sector || !sector.flights || !sector.flights.segments) return
        const { segments } = sector.flights
        const { layoverDurationMap = [] } = sector
        ;(layoverDurationMap as number[]).forEach((layoverMins: number, idx: number) => {
            if (!segments[idx] || !segments[idx + 1]) return
            const city = segments[idx].arrival?.airportName?.c || segments[idx].arrival?.airportCode || ''
            layovers.push({
                durationMins: layoverMins,
                durationStr: `${Math.floor(layoverMins / 60)}h ${layoverMins % 60}m`,
                city
            })
        })
    })
    return layovers
}

export function extractFlightCardProps(
    flight: FlightCard,
    sectors: Sectors,
    getFlightData: (f: FlightCard) => GetFlightDataReturn,
    isPerPaxPricingEnabled: boolean
): ExtractedFlightCardProps {
    const flightData = getFlightData ? getFlightData(flight) : (flight as unknown as ExtractedFlightCardProps)
    return {
        airlineName: flightData.airlineName || '-',
        departureTime: flightData.departureTime || '-',
        arrivalTime: flightData.arrivalTime || '-',
        airlineCodes: flightData.airlineCodes || [],
        stops: typeof flightData.stops === 'number' ? flightData.stops : 0,
        totalDuration: typeof flightData.totalDuration === 'number' ? flightData.totalDuration : 0,
        via: flightData.via,
        price: flightData.price || 0,
        discount: flightData.discount || '',
        arrivalAirport: flightData.arrivalAirport,
        departureAirport: flightData.departureAirport,
        isNbDeparture: flightData.isNbDeparture,
        isNbArrival: flightData.isNbArrival,
        layovers: getLayoversForFlight(flight, sectors),
        seatsLeft: (flight as FlightCard).indicators?.seatsLeft,
        noBaggage: (flight as FlightCard).indicators?.noBaggage,
        longLayover: (flight as FlightCard).indicators?.longLayover,
        shortLayover: (flight as FlightCard).indicators?.shortLayover,
        strikeOffPrice: isPerPaxPricingEnabled
            ? (flight as FlightCard).priceBreakup?.fare?.per_adult_pricing?.strikeOffDetails?.strikedOffPrice
            : (flight as FlightCard).priceBreakup?.fare?.strike_off_details?.strikedOffPrice,
        coupon_detail: (flight as FlightCard).priceBreakup?.fare?.coupon_detail,
        arrivalDate: (flight as FlightCard)?.lastArrival?.date || '',
        departureDate: (flight as FlightCard)?.firstDeparture?.date || ''
    }
}

// Generic getFlightData utility for both desktop and mobile
interface GetFlightDataProps {
    flight: FlightCard
    airline_names: Record<string, string>
    sectors: Sectors
    isRtSolution?: boolean
    isPerPaxPricingEnabled?: boolean
    translationsFn: any
}

export interface GetFlightDataReturn {
    airlineName: string
    departureTime: string
    arrivalTime: string
    stops: number
    airlineCodes: string[]
    totalDuration: number
    via: string
    price: number
    discount: string
    arrivalAirport: string
    departureAirport: string
    isNbDeparture?: boolean
    isNbArrival?: boolean
    strikeOffPrice?: number
    promos?: PromoData[]
}
export function getFlightData({
    flight,
    airline_names,
    sectors,
    isRtSolution,
    translationsFn,
    isPerPaxPricingEnabled
}: GetFlightDataProps): GetFlightDataReturn {
    const { airlineCodes = [], sectorKeys, priceBreakup, totalDuration, nbAirports } = flight || {}

    const airlineName =
        airlineCodes.length > 1 ? translationsFn?.('multipleAirlines') : airline_names[airlineCodes[0]] || ''
    let via = ''
    const sector = sectors[sectorKeys?.[0]] || {}
    const {
        stops,
        totalDuration: durationObj = { hh: 0, mm: 0 },
        flights: { segments = [] } = {},
        firstDeparture,
        lastArrival
    } = sector
    const departureTime = firstDeparture?.time || ''
    const arrivalTime = lastArrival?.time || ''
    const departureAirport = firstDeparture?.airportCode || ''
    const arrivalAirport = lastArrival?.airportCode || ''
    const isNbDeparture = nbAirports && nbAirports.includes(departureAirport)
    const isNbArrival = nbAirports && nbAirports.includes(arrivalAirport)
    if (segments.length > 1) {
        const viaSegments: string[] = []
        segments.forEach(segment => {
            const { arrival } = segment
            viaSegments.push(arrival.airportCode)
        })
        via = viaSegments.slice(0, -1).join(' - ')
    }
    const price = isRtSolution
        ? priceBreakup?.multiAirlinePrice!
        : isPerPaxPricingEnabled
          ? priceBreakup?.totalAdultFare!
          : priceBreakup?.pr || 0
    const strikeOffPrice = isRtSolution ? priceBreakup?.multiAirlinePriceWoDiscount! : undefined
    const discount = priceBreakup?.ds ? `₹${priceBreakup.ds.toLocaleString()}` : ''
    const promos = flight?.promos || []
    return {
        airlineName,
        departureTime,
        arrivalTime,
        stops,
        airlineCodes,
        totalDuration,
        via,
        price,
        discount,
        arrivalAirport,
        departureAirport,
        isNbDeparture,
        isNbArrival,
        strikeOffPrice,
        promos
    }
}
